/**
 * Parties - the front door.
 *
 * The list is server state, so it lives in TanStack Query; socket pushes
 * write straight into the cache rather than triggering a refetch, which is
 * what keeps a boost landing on the card that is already on screen.
 */

import { useEffect, useRef, useState } from 'react';
import { copyText } from '@/lib/clipboard';
import { Avatar } from '@/components/Avatar';
import { RoleTag, UserName, openUserCard } from '@/components/user';
import { Tip } from '@/components/HoverCard';
import { useContextMenu, type MenuItem } from '@/components/Menu';
import { gameBanner, gameIconArt } from '@/lib/gameArt';
import { PartyAlerts } from '@/components/PartyAlerts';
import { JoinWithFriend } from '@/components/PartyExtras';
import { LENGTH_LABEL } from '@/components/PartyCard';
import { PLATFORMS } from '@/components/CreateParty';
import { loosenSuggestions } from '@/lib/match';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints, socket } from '@/lib/api';
import type { Party } from '@/lib/types';

import { go, useQueryState, useRoute } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { Sheet } from '@/components/Sheet';
import { useVoice } from '@/stores/voice';
import { useNarrow } from '@/components/TabBar';
import { MobileMe } from '@/components/MobileMe';
import * as Icons from '@/icons';
import { PartyCard } from '@/components/PartyCard';
import { CreateParty } from '@/components/CreateParty';
import { InvitesPanel, InvitesStrip } from '@/components/Invites';
import { Button, Field, Modal, Textarea, useConfirm, useToast, Empty } from '@/components/ui';
import { Select } from '@/components/Select';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { EmojiText } from '@/components/EmojiText';

type Scope = 'all' | 'friends' | 'invited' | 'mine';

const PIN_KEY = 'pogu.parties.pinned';
const HIDE_KEY = 'pogu.parties.hidden';

function readList(key: string): string[] {
  try { return JSON.parse(localStorage.getItem(key) ?? '[]'); }
  catch { return []; }
}

/** Writes the list and hands it straight back, so it can be used in a setter. */
function writeList(key: string, next: string[]): string[] {
  try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage off */ }
  return next;
}

/** The orders the server knows how to sort by. */
const PARTY_SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'filling', label: 'Almost full' },
  { value: 'emptiest', label: 'Most space left' },
  { value: 'voice', label: 'Most in voice' },
  { value: 'biggest', label: 'Biggest party' },
  { value: 'title', label: 'Title A-Z' },
  { value: 'game', label: 'Grouped by game' },
  { value: 'oldest', label: 'Oldest first' }
];

export function PartiesView() {
  /*
   * Every filter lives in the address.
   *
   * It used to be a row of useState hooks with one useEffect feeding scope
   * from the route, which meant scope worked and nothing else did. Opening
   * #/parties?game=valorant from the home page set the address, left the
   * select reading "All games", and listed all eleven parties - the link was
   * ignored entirely rather than merely mislabelled.
   *
   * With the address as the source of truth the control and the results read
   * the same value, so they cannot disagree; a shared link opens filtered;
   * refresh keeps the filter; and Back steps through the choices made.
   */
  /* Read-only here: the sidebar changes scope by navigating, which is the
     same address this reads from. */
  const [scope] = useQueryState('scope', 'all', {
    allow: ['all', 'friends', 'invited', 'mine']
  }) as unknown as [Scope];
  const [game, setGame] = useQueryState('game');
  const [region, setRegion] = useQueryState('region');
  const [sort, setSort] = useQueryState('sort', 'newest', {
    allow: PARTY_SORTS.map((s) => s.value)
  });

  /*
   * Search is the exception that proves the rule: it is in the address so it
   * can be linked and survives refresh, but typing must not leave a history
   * entry per keystroke - push: false - and the input stays local so it does
   * not lag a character behind the address.
   */
  const [search, setSearch] = useQueryState('q', '', { push: false });
  const [typed, setTyped] = useState(search);
  // Defaults to hiding full parties: a list you cannot join is not a list of
  // options, and this is the browser rather than an archive.
  const [openOnly, setOpenOnly] = useState(true);
  const [micOnly, setMicOnly] = useState(false);
  /* The three narrower questions people actually ask: how long, on what, and
     whether somebody new is welcome. */
  const [length, setLength] = useState('');
  const [platform, setPlatform] = useState('');
  const [newcomers, setNewcomers] = useState(false);
  /* Parties where a microphone is optional and the chat is on - the ones
     somebody who does not want to talk can actually join. */
  const [textOnly, setTextOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [reporting, setReporting] = useState<Party | null>(null);
  /* The party you are joining as a pair, if you are. */
  const [pairing, setPairing] = useState<Party | null>(null);
  const [pageNo, setPageNo] = useState(1);
  // The card you clicked. Details open beside the grid rather than leaving it.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /*
   * Pinned and hidden parties, kept on this device.
   *
   * Neither is anybody else's business: pinning is how you keep the party
   * you are watching at the top of a busy list, and hiding is for the one
   * you have already decided against and do not want to keep reading.
   */
  const [pinned, setPinned] = useState<string[]>(() => readList(PIN_KEY));
  const [hidden, setHidden] = useState<string[]>(() => readList(HIDE_KEY));
  const [showHidden, setShowHidden] = useState(false);

  function togglePin(partyId: string) {
    setPinned((list) => writeList(PIN_KEY, list.includes(partyId) ? list.filter((x) => x !== partyId) : [...list, partyId]));
  }
  function toggleHidden(partyId: string) {
    setHidden((list) => writeList(HIDE_KEY, list.includes(partyId) ? list.filter((x) => x !== partyId) : [...list, partyId]));
  }

  // Ctrl+N and the palette both arrive as ?create=1 rather than reaching in
  // here, so the shortcut works from any screen. The flag is stripped once
  // read - a refresh should not reopen the dialog.
  useEffect(() => {
    if (typed.trim() === search) return;
    const id = setTimeout(() => setSearch(typed.trim()), 240);
    return () => clearTimeout(id);
  }, [typed, search, setSearch]);

  /* Back, a shared link, or Clear filters all change the address without
     touching the input, so the input follows it. */
  useEffect(() => {
    setTyped((current) => (current.trim() === search ? current : search));
  }, [search]);

  const route = useRoute();
  /* What a Create party link asked for: an event's game and title, kept
     across the address being tidied away. */
  const [seed, setSeed] = useState<{ gameId?: string; title?: string } | null>(null);
  useEffect(() => {
    if (route.query.create) {
      setSeed({ gameId: route.query.game || undefined, title: route.query.title || undefined });
      setCreating(true);
    }
    else if (route.query.invites) setInvitesOpen(true);
    else return;
    history.replaceState(null, '', '#/parties');
  }, [route.query.create, route.query.invites, route.query.game, route.query.title]);
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const voice = useVoice();
  const narrow = useNarrow();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  async function closeParty(party: Party) {
    const ok = await confirm('Close ' + party.title + '?', 'Everyone is dropped out, including from voice.', {
      confirmLabel: 'Close party',
      danger: true
    });
    if (!ok) return;
    try {
      await api.del('/api/parties/' + party.id);
      toast('Party closed', 'good');
      await queryClient.invalidateQueries({ queryKey: ['parties'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  /**
   * Boosting, once every few seconds.
   *
   * Each press broadcasts to everyone looking at the list, so a held-down
   * button turns into a storm of sockets. The cooldown is per party and lives
   * in a ref, because blocking on state would re-render the list to do it.
   */
  const boostCooldown = useRef<Record<string, number>>({});

  async function boost(party: Party) {
    const now = Date.now();
    const ready = boostCooldown.current[party.id] ?? 0;
    if (now < ready) {
      toast('Give it a second', 'warn');
      return;
    }
    boostCooldown.current[party.id] = now + 3000;

    try {
      await api.post('/api/admin/parties/' + party.id + '/boost', { boosted: !party.boosted });
      toast(party.boosted ? 'Boost removed' : 'Boosted to the top of every list', 'good');
    } catch (err) {
      boostCooldown.current[party.id] = 0;
      toast((err as Error).message, 'bad');
    }
  }

  /** Right-click on a party. Built fresh each time so it matches the state now. */
  function partyMenu(party: Party): MenuItem[] {
    const staff = me && me.role !== 'user';
    const isOwner = party.hostId === me?.id;
    const canJoin = !party.isMember && party.memberCount < party.size;

    const items: MenuItem[] = [
      { label: 'Open party', icon: 'external', onClick: () => go('party/' + party.id) }
    ];
    items.push({ label: 'Report party', icon: 'flag', onClick: () => setReporting(party) });

    if (canJoin) {
      items.push({ label: 'Join party', icon: 'addFriend', onClick: () => join.mutate(party.id) });
      /* Two seats at once, for two people who came together. */
      if (party.size - party.memberCount >= 2) {
        items.push({ label: 'Join with a friend', icon: 'friends', onClick: () => setPairing(party) });
      }
      // Straight into the call, without stopping at the party page first.
      if (party.voice?.enabled !== false) {
        items.push({
          label: 'Join and connect to voice',
          icon: 'callJoin',
          onClick: () => join.mutate(party.id, { onSuccess: () => { void voice.join(party.id); } })
        });
      }
    }

    if (party.isMember) {
      items.push({ label: 'Open the chat', icon: 'chat', onClick: () => go('party/' + party.id) });
      if (voice.partyId !== party.id && party.voice?.enabled !== false) {
        items.push({ label: 'Connect to voice', icon: 'callJoin', onClick: () => void voice.join(party.id) });
      }
      if (voice.partyId === party.id) {
        items.push({ label: 'Disconnect from voice', icon: 'callLeave', onClick: () => void voice.leave() });
      }
    }

    items.push('-');
    items.push({
      label: pinned.includes(party.id) ? 'Unpin from the top' : 'Pin to the top',
      icon: 'star',
      onClick: () => togglePin(party.id)
    });
    items.push({
      label: hidden.includes(party.id) ? 'Show this party again' : 'Hide this party',
      icon: hidden.includes(party.id) ? 'eye' : 'eyeOff',
      onClick: () => toggleHidden(party.id)
    });
    items.push({
      label: 'Only ' + (catalog?.games.find((g) => g.id === party.gameId)?.name ?? party.gameId),
      icon: 'squads',
      onClick: () => setGame(party.gameId)
    });

    const host = party.members.find((m) => m.userId === party.hostId)?.user;
    if (host && host.id !== me?.id) {
      items.push('-');
      items.push({ label: 'View ' + host.displayName, icon: 'profile', onClick: () => go('u/' + host.username) });
      items.push({ label: 'Message the host', icon: 'chat', onClick: () => go('chat/' + host.username) });
    }

    items.push('-');
    items.push({
      label: 'Copy link',
      icon: 'link',
      onClick: () => {
        void copyText(location.origin + '/#/party/' + party.id);
        toast('Link copied', 'good');
      }
    });
    items.push({
      label: 'Copy party ID',
      icon: 'copy',
      onClick: () => {
        void copyText(party.id);
        toast('ID copied', 'good');
      }
    });

    // The owner manages the party from here rather than having to open it.
    if (isOwner) {
      items.push('-');
      items.push({ label: 'Manage party', icon: 'sliders', onClick: () => go('party/' + party.id) });
      items.push({ label: 'Edit details', icon: 'edit', onClick: () => go('party/' + party.id + '?edit=1') });
      items.push({ label: 'Invite people', icon: 'addFriend', onClick: () => go('party/' + party.id + '?invite=1') });
      items.push({ label: 'Close party', icon: 'trash', danger: true, onClick: () => void closeParty(party) });
    }

    if (staff) {
      items.push('-');
      items.push({
        label: party.boosted ? 'Remove boost' : 'Boost to the top',
        icon: 'bolt',
        onClick: () => void boost(party)
      });
      if (!isOwner) {
        items.push({ label: 'Delete party', icon: 'trash', danger: true, onClick: () => void closeParty(party) });
      }
    }

    return items;
  }

  const queryClient = useQueryClient();

  const key = ['parties', scope, game, region, search, sort, openOnly, micOnly, length, platform, newcomers, textOnly] as const;
  const connection = useSession((s) => s.connection);
  /*
   * Whether anything is open at all.
   *
   * The filtered list cannot tell "every party is full" from "there are no
   * parties" - both come back empty when Has room is on - so the empty state
   * was telling a brand-new server that everything running was full. This is
   * the same unfiltered list Home and Games ask for, under the same key, so it
   * shares their request rather than adding one.
   */
  const anyOpen = useQuery({
    queryKey: ['parties', 'all'],
    queryFn: () => endpoints.parties({ scope: 'all' }),
    staleTime: 15_000
  });
  const somethingOpen = (anyOpen.data?.parties ?? []).some((p) => !p.closed);

  const { data, isPending, isError, refetch, isFetching } = useQuery({
    queryKey: key,
    queryFn: () => endpoints.parties({
      scope,
      game: game || undefined,
      region: region || undefined,
      q: search || undefined,
      sort,
      open: openOnly ? 'true' : undefined,
      mic: micOnly ? 'true' : undefined,
      length: length || undefined,
      platform: platform || undefined,
      newcomers: newcomers ? 'true' : undefined,
      text: textOnly ? 'true' : undefined
    }),
    placeholderData: (previous) => previous
  });

  /* A boost has to land on the card that is already on screen, and be seen
     landing - re-sorting the list silently would look like nothing happened. */
  const [justBoosted, setJustBoosted] = useState<string | null>(null);

  useEffect(() => {
    const off = [
      socket.on('party:boosted', (payload: { partyId: string; boosted: boolean }) => {
        queryClient.setQueriesData<{ parties: Party[]; total: number }>(
          { queryKey: ['parties'] },
          (old) => old && {
            ...old,
            parties: old.parties.map((p) =>
              p.id === payload.partyId ? { ...p, boosted: payload.boosted } : p)
          }
        );
        if (payload.boosted) {
          setJustBoosted(payload.partyId);
          setTimeout(() => setJustBoosted(null), 1400);
        }
        // Deliberately no refetch. The patch above already holds what the
        // server would send, and re-fetching re-rendered every card in the
        // list a second later - which is the flicker you see after boosting.
        // The next natural refetch reconciles the ordering.
      }),
      socket.on('party:created', () => queryClient.invalidateQueries({ queryKey: ['parties'] })),
      socket.on('party:closed', () => queryClient.invalidateQueries({ queryKey: ['parties'] })),
      socket.on('party:updated', (payload: { party: Party }) => {
        queryClient.setQueriesData<{ parties: Party[]; total: number }>(
          { queryKey: ['parties'] },
          (old) => old && {
            ...old,
            parties: old.parties.map((p) => (p.id === payload.party.id ? { ...p, ...payload.party } : p))
          }
        );
      })
    ];
    return () => off.forEach((fn) => fn());
  }, [queryClient]);

  const join = useMutation({
    mutationFn: (id: string) => endpoints.joinParty(id),
    onSuccess: (_res, id) => {
      void queryClient.invalidateQueries({ queryKey: ['parties'] });
      go('party/' + id);
    }
  });

  const all = data?.parties ?? [];
  // Hidden ones drop out entirely unless you ask for them back; pinned ones
  // float to the top of whatever order the server sent.
  const parties = all
    .filter((p) => showHidden || !hidden.includes(p.id))
    .slice()
    .sort((a, b) => Number(pinned.includes(b.id)) - Number(pinned.includes(a.id)));

  // Twenty-four to a page: four across, and you scroll through six rows
  // before there is a next page to reach for. Paging at eight meant reaching
  // for the arrows constantly on a busy evening.
  const perPage = 24;
  const pageCount = Math.max(1, Math.ceil(parties.length / perPage));
  const page = Math.min(pageNo, pageCount);
  const shown = parties.slice((page - 1) * perPage, page * perPage);
  // Looked up fresh each render, so socket updates flow into the open panel.
  const selected = selectedId ? parties.find((p) => p.id === selectedId) ?? null : null;

  return (
    <>
      <div className="cols-desktop flex min-h-0 flex-1">
        {/* the browsing column */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-4">

          {narrow ? <MobileMe /> : null}

          {/* The hero.

              It used to make three claims - trusted hosts, voice ready,
              better matches - none of which anything in the app measured.
              These are counted off the list on screen, so they are true by
              construction and they change as the evening does. */}
          <DiscoveryHeader
            narrow={narrow}
            typed={typed}
            onTyped={setTyped}
            onCreate={() => go('parties?create=1')}
          />
          <InvitesStrip />
          {/* one row of filters, bare on the page */}
          {/* One row on a phone, scrolled sideways - three rows of wrapped
              pills pushed the parties themselves off the screen. */}
          <div className={cn(
            'flex flex-none items-center gap-2',
            narrow ? 'no-scrollbar -my-2 -mx-4 overflow-x-auto px-4 py-2 [&>*]:flex-none' : 'flex-wrap'
          )}>
            <Select
              auto
              lead="squads"
              aria-label="Game"
              className="min-w-[140px]"
              value={game}
              onChange={setGame}
              options={[
                { value: '', label: 'All games' },
                ...(catalog?.games ?? []).map((g) => ({ value: g.id, label: g.name, icon: g.icon, color: g.color, art: gameIconArt(g.id) ?? undefined }))
              ]}
            />
            <Select
              auto
              lead="globe"
              aria-label="Region"
              className="min-w-[130px]"
              value={region}
              onChange={setRegion}
              options={[
                { value: '', label: 'All regions' },
                ...(catalog?.regions ?? []).map((r) => ({ value: r.id, label: r.name }))
              ]}
            />
            <Select
              auto
              lead="sort"
              aria-label="Sort parties"
              search={false}
              className="min-w-[140px]"
              value={sort}
              onChange={setSort}
              options={PARTY_SORTS}
            />
            <Toggle2 on={openOnly} icon="users" onClick={() => setOpenOnly(!openOnly)}>Has room</Toggle2>
            <Toggle2 on={micOnly} icon="mic" onClick={() => setMicOnly(!micOnly)}>Mic required</Toggle2>
            <Toggle2 on={newcomers} icon="heart" onClick={() => setNewcomers(!newcomers)}>Beginner friendly</Toggle2>
            <Toggle2 on={textOnly} icon="chat" onClick={() => { setTextOnly(!textOnly); if (!textOnly) setMicOnly(false); }}>Text friendly</Toggle2>
            <Select
              auto
              lead="clock"
              aria-label="How long"
              search={false}
              className="min-w-[140px]"
              value={length}
              onChange={setLength}
              options={[
                { value: '', label: 'Any length' },
                { value: 'one', label: 'One match' },
                { value: 'short', label: 'About an hour' },
                { value: 'long', label: 'Long session' }
              ]}
            />
            <Select
              auto
              lead="monitor"
              aria-label="Platform"
              search={false}
              className="min-w-[130px]"
              value={platform}
              onChange={setPlatform}
              options={[{ value: '', label: 'Any platform' }, ...PLATFORMS.map((p) => ({ value: p.id, label: p.label }))]}
            />
            {hidden.length ? (
              <Toggle2 on={showHidden} icon="eye" onClick={() => setShowHidden(!showHidden)}>
                {hidden.length} hidden
              </Toggle2>
            ) : null}
          </div>

          {/*
            What is currently narrowing the list, and how to undo it.

            The controls above show their own state, but once there are five of
            them you stop reading them as a group - which is how people end up
            staring at an empty list without noticing they set a filter three
            screens ago. These chips say it once, in one place, and every one
            of them removes itself.
          */}
          <ActiveFilters
            game={game}
            region={region}
            search={search}
            micOnly={micOnly}
            openOnly={openOnly}
            catalog={catalog}
            onClear={(what) => {
              if (what === 'game') setGame('');
              if (what === 'region') setRegion('');
              if (what === 'search') { setTyped(''); setSearch(''); }
              if (what === 'mic') setMicOnly(false);
              if (what === 'open') setOpenOnly(false);
              if (what === 'all') {
                setGame(''); setRegion(''); setTyped(''); setSearch('');
                setMicOnly(false); setOpenOnly(false);
              }
            }}
          />

          {isPending && !data ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="h-[300px] animate-pulse rounded-[var(--radius-l)] bg-surface-1" />
              ))}
            </div>
          ) : isError ? (
            /*
             * Failed, and why it probably failed.
             *
             * This used to print the raw error message. A player cannot act on
             * "Failed to fetch"; they can act on "you are offline" or "try
             * again", so that is what it says. The real error is still in the
             * network panel for anyone debugging.
             */
            <Empty
              role="alert"
              icon={<Icon name="users" size={26} />}
              title={connection === 'closed' || connection === 'reconnecting' ? 'You are offline' : 'Parties did not load'}
              note={connection === 'closed' || connection === 'reconnecting'
                ? 'Pugo cannot reach the server right now. The list will come back when the connection does.'
                : 'Something went wrong fetching the list. Everything else is still working.'}
              actions={
                <Button kind="primary" disabled={isFetching} onClick={() => void refetch()}>
                  {isFetching ? 'Trying…' : 'Try again'}
                </Button>
              }
            />
          ) : parties.length === 0 ? (
            /*
             * Empty, and which kind of empty.
             *
             * One "No parties found" used to cover every case - so a Valorant
             * search with no results told you to "be the first to post one" as
             * if nobody were playing anything. The filters narrowing the list,
             * the free-seats toggle hiding full parties, and a genuinely quiet
             * evening each get the next step that fits.
             */
            (game || region || search || micOnly) ? (
              <Empty
                role="status"
                icon={<Icons.Search size={26} />}
                title="No parties match these filters"
                note="Nothing open fits everything you picked. Loosen a filter, or start the party you were looking for."
                actions={
                  <>
                    <Button
                      kind="outline"
                      onClick={() => { setGame(''); setRegion(''); setTyped(''); setSearch(''); setMicOnly(false); setLength(''); setPlatform(''); setNewcomers(false); setTextOnly(false); }}
                    >
                      Clear filters
                    </Button>
                    <Button kind="primary" icon={<Icons.Plus size={16} />} onClick={() => setCreating(true)}>
                      Create party
                    </Button>
                    {/* One thing at a time, so you keep the part you meant. */}
                    {loosenSuggestions({
                      game, region, search, micOnly, length, platform, newcomers,
                      gameName: catalog?.games.find((g) => g.id === game)?.name,
                      regionName: catalog?.regions.find((r) => r.id === region)?.name,
                      clear: (what) => {
                        if (what === 'game') setGame('');
                        else if (what === 'region') setRegion('');
                        else if (what === 'search') { setTyped(''); setSearch(''); }
                        else if (what === 'mic') setMicOnly(false);
                        else if (what === 'length') setLength('');
                        else if (what === 'platform') setPlatform('');
                        else setNewcomers(false);
                      }
                    }).map((row) => (
                      <Button key={row.id} kind="plain" onClick={row.apply}>{row.label}</Button>
                    ))}
                    {/* Or hand the search to the server and get on with your evening. */}
                    <PartyAlerts gameId={game} region={region} />
                  </>
                }
              />
            ) : openOnly && scope === 'all' && somethingOpen ? (
              <Empty
                role="status"
                icon={<Icon name="users" size={26} />}
                title="No parties with free seats"
                note="Anything running right now is full. You can still look at full parties, or start one of your own."
                actions={
                  <>
                    <Button kind="outline" onClick={() => setOpenOnly(false)}>Show full parties</Button>
                    <Button kind="primary" icon={<Icons.Plus size={16} />} onClick={() => setCreating(true)}>
                      Create party
                    </Button>
                  </>
                }
              />
            ) : scope === 'all' ? (
              <Empty
                role="status"
                icon={<Icon name="users" size={26} />}
                title="Nothing open right now"
                note="It is quiet. A party you post appears here and on everyone's Home straight away."
                actions={
                  <Button kind="primary" icon={<Icons.Plus size={16} />} onClick={() => setCreating(true)}>
                    Create party
                  </Button>
                }
              />
            ) : (
              <Empty
                role="status"
                icon={<Icon name="users" size={26} />}
                title="No parties here"
                note="Nothing in this list yet. All parties shows everything that is open."
              />
            )
          ) : (
            <>
              <div className="flex flex-none items-center gap-2">
                <span className="text-[12.5px] font-semibold text-mute">
                  {parties.length} {parties.length === 1 ? 'party' : 'parties'} available
                </span>
                <span className="flex-1" />
                <span className="flex items-center gap-0.5 rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft p-0.5">
                  {([['grid', 'grid'], ['list', 'list']] as const).map(([id, icon]) => (
                    <button
                      key={id}
                      type="button"
                      title={id === 'grid' ? 'Grid' : 'List'}
                      onClick={() => setView(id)}
                      className={cn(
                        'grid h-7 w-7 place-items-center rounded-[calc(6px*var(--radius-mul,1))] transition-colors',
                        view === id ? 'bg-surface-3 text-ink' : 'text-mute hover:text-dim'
                      )}
                    >
                      <Icon name={icon} size={14} />
                    </button>
                  ))}
                </span>
              </div>

              <div className={cn(
                'stagger grid flex-none',
                view === 'list' ? 'gap-2' : 'gap-4',
                view === 'list'
                  ? '[grid-template-columns:1fr]'
                  // The details panel takes a third of the row, so the grid
                  // drops a column while it is open rather than squeezing
                  // three cards into the space left over.
                  : narrow
                    ? '[grid-template-columns:1fr]'
                    : selectedId
                      ? 'grid-cols-1 lg:grid-cols-2'
                      : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
              )}>
                {shown.map((party) => (
                  <PartyCard
                    key={party.id}
                    party={party}
                    compact={narrow}
                    row={view === 'list' && !narrow}
                    selected={selectedId === party.id}
                    justBoosted={justBoosted === party.id}
                    onOpen={() => setSelectedId(party.id)}
                    onJoin={() => join.mutate(party.id)}
                    menu={() => partyMenu(party)}
                  />
                ))}
              </div>

              <Pager
                page={page}
                pageCount={pageCount}
                from={(page - 1) * perPage + 1}
                to={Math.min(page * perPage, parties.length)}
                total={parties.length}
                onPage={setPageNo}
              />
            </>
          )}
        </div>

        {/* the details panel: a card opens here, not a new screen */}
        {selected ? (
          <PartySide
            party={selected}
            pinned={pinned.includes(selected.id)}
            onClose={() => setSelectedId(null)}
            onJoin={() => join.mutate(selected.id)}
            onPin={() => togglePin(selected.id)}
            menu={() => partyMenu(selected)}
          />
        ) : null}
      </div>

      {dialog}
      {reporting ? <ReportParty party={reporting} onClose={() => setReporting(null)} /> : null}
      {creating ? <CreateParty seed={seed ?? undefined} onClose={() => { setCreating(false); setSeed(null); }} /> : null}
      {pairing ? <JoinWithFriend party={pairing} onClose={() => setPairing(null)} /> : null}
      {invitesOpen ? <InvitesPanel onClose={() => setInvitesOpen(false)} /> : null}
    </>
  );
}

/**
 * Paging under the grid.
 *
 * Centred, with the range spelled out under it, because "Showing 1-8 of 34"
 * answers the question the arrows raise. It draws nothing when everything
 * already fits on one page.
 */
function Pager({ page, pageCount, from, to, total, onPage }: {
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
  onPage: (next: number) => void;
}) {
  if (pageCount < 2) return null;
  // A long list gets a window around the current page rather than every
  // number, so the row keeps its width.
  const first = Math.max(1, Math.min(page - 2, pageCount - 4));
  const numbers = Array.from({ length: Math.min(5, pageCount) }, (_, i) => first + i);

  return (
    <div className="mt-[calc(var(--gap)*.6)] flex flex-none flex-col items-center gap-2 pb-1">
      <div className="flex items-center gap-1.5">
        <PagerBtn label="Previous page" icon="chevronL" disabled={page === 1} onClick={() => onPage(page - 1)} />
        {numbers.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onPage(n)}
            className={cn(
              'grid h-[34px] min-w-[34px] place-items-center rounded-[calc(10px*var(--radius-mul,1))] px-2 text-[13px] font-bold tabular-nums',
              'border transition-[background,border-color,color,box-shadow] duration-150',
              n === page
                ? 'border-accent bg-accent-soft text-ink shadow-[0_0_16px_var(--glow)]'
                : 'border-line-soft bg-surface-1 text-mute hover:border-line hover:text-dim'
            )}
          >
            {n}
          </button>
        ))}
        <PagerBtn label="Next page" icon="chevronR" disabled={page === pageCount} onClick={() => onPage(page + 1)} />
      </div>
      <span className="text-[12.5px] text-mute tabular-nums">
        Showing {from}–{to} of {total} {total === 1 ? 'party' : 'parties'}
      </span>
    </div>
  );
}

function PagerBtn({ label, icon, disabled, onClick }: {
  label: string;
  icon: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'grid h-[34px] w-[34px] place-items-center rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-surface-1 text-mute',
        'transition-[background,border-color,color] duration-150',
        'hover:border-line hover:text-ink disabled:pointer-events-none disabled:opacity-35'
      )}
    >
      <Icon name={icon} size={16} stroke={2.2} />
    </button>
  );
}

/** A filter that is either on or off. Reads as a switch, not a button. */
function Toggle2({ on, icon, onClick, children }: {
  on: boolean;
  icon: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-[46px] flex-none items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] px-4 text-[13px] font-semibold outline-none',
        'transition-[background-color,color,box-shadow] duration-[250ms] ease-[var(--ease-out)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        on
          ? 'bg-accent text-accent-fg shadow-[0_8px_20px_-10px_color-mix(in_srgb,var(--accent)_80%,transparent)]'
          : 'bg-surface-1 text-ink shadow-[inset_0_0_0_1px_var(--line)] hover:bg-surface-2'
      )}
    >
      <Icon name={icon} size={16} />
      {children}
    </button>
  );
}

/**
 * Reporting a party.
 *
 * Aimed at what a party puts in front of everyone - a name meant to harm, or
 * what is written in its description. The report lands on whoever posted it,
 * which is who a moderator would act on anyway.
 */
const PARTY_REASONS = [
  ['harassment', 'Harassment or a name meant to harm'],
  ['inappropriate', 'Swearing or inappropriate content'],
  ['spam', 'Spam or advertising'],
  ['scam', 'Scam or phishing'],
  ['impersonation', 'Pretending to be someone else'],
  ['other', 'Something else']
] as const;

function ReportParty({ party, onClose }: { party: Party; onClose: () => void }) {
  const [reason, setReason] = useState<string>('harassment');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function send() {
    setBusy(true);
    try {
      await api.post('/api/reports', { partyId: party.id, reason, note });
      toast('Report sent to the moderators', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Report this party"
      subtitle="Moderators see the reason, your note, and who sent it."
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="danger" disabled={busy} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send report'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-[var(--gap)]">
        <div className="rounded-[var(--radius-s)] border border-line-soft bg-surface-1 p-2.5">
          <div className="text-[13.5px] font-bold"><EmojiText text={party.title} size={16} /></div>
          {party.description ? (
            <p className="mt-1 text-[12.5px] whitespace-pre-wrap text-mute"><EmojiText text={party.description} size={15} /></p>
          ) : null}
        </div>

        <Field label="What is wrong with it?">
          <Select
            lead="flag"
            search={false}
            value={reason}
            onChange={setReason}
            options={PARTY_REASONS.map(([id, label]) => ({ value: id, label }))}
          />
        </Field>

        <Field label="Anything else?" hint="Optional, but it helps.">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="What did you see?"
          />
        </Field>
      </div>
    </Modal>
  );
}

/** One of the three things the hero promises. Icon, label, one line under it. */
/**
 * The banner at the top of the browse screen.
 *
 * The three figures are counted from the parties actually on the page. A
 * quiet Tuesday should look like a quiet Tuesday - a banner that always says
 * the same three things is wallpaper, and people stop reading it.
 */

/** One number and what it counts. */

/**
 * The party details panel./**
 * The party details panel.
 *
 * Opens beside the grid when you pick a card, rather than replacing the screen
 * you were browsing - you almost always want to look at two or three before
 * committing, and a page navigation loses your place in the list every time.
 *
 * Everything the card had to leave out lives here, with room to breathe.
 */
function PartySide({ party, pinned, onClose, onJoin, onPin, menu }: {
  party: Party;
  pinned: boolean;
  onClose: () => void;
  onJoin: () => void;
  onPin: () => void;
  menu: () => MenuItem[];
}) {
  const catalog = useSession((s) => s.catalog);
  const narrow = useNarrow();
  const ctx = useContextMenu(menu);
  const game = catalog?.games.find((g) => g.id === party.gameId);
  const banner = gameBanner(party.gameId);
  const host = party.members.find((m) => m.userId === party.hostId)?.user ?? null;
  const regionName = catalog?.regions.find((r) => r.id === party.region)?.name ?? party.region;
  const rank = party.minRank && party.maxRank
    ? party.minRank + ' – ' + party.maxRank
    : party.minRank || party.maxRank || 'Any';
  const open = party.size - party.memberCount;

  /* Everything the panel shows, in whichever container it lands in. */
  const body = (
    <>
      {/* the banner, with the close on top of it */}
      <div className="relative h-[132px] flex-none overflow-hidden">
        {banner ? (
          <img src={banner} alt="" className="h-full w-full object-cover" />
        ) : (
          <span
            className="absolute inset-0"
            style={{ background: 'linear-gradient(135deg, color-mix(in srgb, ' + (game?.color ?? 'var(--accent)') + ' 55%, var(--art-base)), var(--art-base))' }}
          />
        )}
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-surface-side via-[color-mix(in_srgb,var(--bg-side)_35%,transparent)] to-transparent" />
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-2.5 right-2.5 grid h-7 w-7 place-items-center rounded-[calc(8px*var(--radius-mul,1))] bg-[rgba(0,0,0,.45)] text-white/80 backdrop-blur-sm transition-colors hover:bg-[rgba(0,0,0,.65)] hover:text-white"
        >
          <Icon name="close" size={14} />
        </button>

        <div className="absolute inset-x-3 bottom-2.5 flex flex-wrap gap-1.5">
          {party.micRequired ? <SideTag tone="voice">Mic required</SideTag> : null}
          {party.sessionLength ? <SideTag>{LENGTH_LABEL[party.sessionLength]}</SideTag> : null}
          {open > 0 ? <SideTag tone="good">{open} open</SideTag> : <SideTag tone="warn">Full</SideTag>}
          {party.boosted ? <SideTag tone="accent">Featured</SideTag> : null}
        </div>
      </div>

      <div className="flex flex-col gap-4 p-4">
        <div>
          <h2 className="text-[18px] leading-tight font-bold"><EmojiText text={party.title} size={21} /></h2>
          <p className="mt-0.5 text-[12.5px] text-mute">
            {game?.name ?? party.gameId} · {party.mode}
          </p>
        </div>

        {host ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openUserCard(host, e.currentTarget); }}
            className="flex items-center gap-2.5 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-2.5 text-left transition-colors hover:border-line hover:bg-surface-2"
          >
            <Avatar user={host} size={34} presence />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <UserName user={host} className="truncate text-[13px] font-bold" />
                <RoleTag role={host.role} />
              </span>
              <span className="block text-[11.5px] text-mute">Host</span>
            </span>
            <Icon name="chevronR" size={14} className="flex-none text-ghost" />
          </button>
        ) : null}

        {party.description ? (
          <div>
            <h3 className="mb-1.5 text-[10.5px] font-bold tracking-[.08em] text-ghost uppercase">
              About this party
            </h3>
            <p className="selectable text-[13px] leading-relaxed text-dim"><EmojiText text={party.description} size={16} /></p>
          </div>
        ) : null}

        <dl className="flex flex-col gap-2.5">
          <SideRow icon="globe" label="Region" value={regionName} />
          {party.language ? <SideRow icon="translate" label="Language" value={party.language} /> : null}
          <SideRow icon="users" label="Party size" value={party.memberCount + ' / ' + party.size} />
          <SideRow icon="trending" label="Required rank" value={rank} />
          <SideRow icon="clock" label="Created" value={ago(party.createdAt)} />
        </dl>

        {party.needRoles?.length ? (
          <div>
            <h3 className="mb-1.5 text-[10.5px] font-bold tracking-[.08em] text-ghost uppercase">
              Roles needed
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {party.needRoles.map((r) => (
                <span key={r} className="rounded-full border border-line-soft bg-surface-1 px-2.5 py-1 text-[11.5px] font-semibold text-dim">
                  {r}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {party.members.length ? (
          <div>
            <h3 className="mb-2 text-[10.5px] font-bold tracking-[.08em] text-ghost uppercase">
              In the party
            </h3>
            <div className="flex flex-col gap-1.5">
              {party.members.map((m) => m.user ? (
                <button
                  key={m.userId}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openUserCard(m.user!, e.currentTarget); }}
                  className="flex items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] px-1.5 py-1 text-left transition-colors hover:bg-surface-2"
                >
                  <Avatar user={m.user} size={26} presence />
                  <UserName user={m.user} className="min-w-0 flex-1 truncate text-[12.5px] font-semibold" />
                  {m.role === 'host' ? <Icon name="crown" size={13} className="flex-none text-mythic" /> : null}
                </button>
              ) : null)}
            </div>
          </div>
        ) : null}
      </div>

      {/* the actions, held at the bottom of the panel */}
      <div className="sticky bottom-0 mt-auto flex flex-none items-center gap-2 border-t border-line-soft bg-surface-side p-3">
        <button
          type="button"
          onClick={() => (party.isMember ? go('party/' + party.id) : onJoin())}
          disabled={!party.isMember && open <= 0}
          className={cn(
            'flex h-[38px] flex-1 items-center justify-center gap-1.5 rounded-[var(--radius)] text-[13.5px] font-bold',
            'transition-[background-color,box-shadow] duration-150 disabled:pointer-events-none disabled:opacity-40',
            'bg-[linear-gradient(180deg,var(--accent-hi),var(--accent))] text-accent-fg',
            'shadow-[inset_0_1px_0_rgba(255,255,255,.18)] hover:bg-accent-hi'
          )}
        >
          <Icon name="bolt" size={15} />
          {party.isMember ? 'Open party' : 'Quick join'}
        </button>
        <Tip text={pinned ? 'Unpin' : 'Pin to the top'}>
          <button
            type="button"
            onClick={onPin}
            aria-label={pinned ? 'Unpin' : 'Pin to the top'}
            className={cn(
              'grid h-[38px] w-[38px] flex-none place-items-center rounded-[var(--radius)] border transition-colors',
              pinned
                ? 'border-accent-line bg-accent-soft text-accent-hi'
                : 'border-line-soft bg-surface-1 text-mute hover:border-line hover:text-ink'
            )}
          >
            <Icon name="bookmark" size={16} />
          </button>
        </Tip>
        <button
          type="button"
          aria-label="More"
          {...ctx.props}
          onClick={(e) => ctx.openAt(e.clientX, e.clientY)}
          className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[var(--radius)] border border-line-soft bg-surface-1 text-mute transition-colors hover:border-line hover:text-ink"
        >
          <Icon name="more" size={16} />
        </button>
        {ctx.menu}
      </div>
    </>
  );

  /*
   * A sheet on a phone, a column on a desktop.
   *
   * Stacked under the grid it was a second scrolling area you had to find
   * by scrolling past everything you were browsing. Coming up from the
   * bottom, with a bar you can pull back down, is how a phone shows you one
   * thing without losing the thing behind it.
   */
  if (narrow) {
    return <Sheet open onClose={onClose} full>{body}</Sheet>;
  }

  return (
    <aside className="panel-in flex w-[330px] flex-none flex-col overflow-y-auto border-l border-line-soft bg-surface-side">
      {body}
    </aside>
  );
}

function SideTag({ tone, children }: { tone?: 'good' | 'warn' | 'voice' | 'accent'; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'rounded-[calc(6px*var(--radius-mul,1))] px-2 py-0.5 text-[10.5px] font-bold backdrop-blur-sm',
        tone === 'good' && 'bg-[color-mix(in_srgb,var(--good)_22%,rgba(0,0,0,.5))] text-good',
        tone === 'warn' && 'bg-[color-mix(in_srgb,var(--warn)_22%,rgba(0,0,0,.5))] text-warn',
        tone === 'voice' && 'bg-[color-mix(in_srgb,var(--voice)_22%,rgba(0,0,0,.5))] text-voice',
        tone === 'accent' && 'bg-[color-mix(in_srgb,var(--accent)_38%,rgba(0,0,0,.4))] text-white',
        !tone && 'bg-[rgba(0,0,0,.5)] text-white/85'
      )}
    >
      {children}
    </span>
  );
}

/**
 * The discovery header.
 *
 * Reference exec-0a6eb6fa. The old hero made three claims the app did not
 * measure, then counted parties instead; both are gone. What is left is what
 * the page is for: a title, a sentence, and the search that most people reach
 * for first.
 *
 * One primary action, which is D06 - the header, the hero and the empty state
 * all used to offer a way to create a party.
 */
function DiscoveryHeader({ narrow, typed, onTyped, onCreate }: {
  narrow: boolean;
  typed: string;
  onTyped: (v: string) => void;
  onCreate: () => void;
}) {
  return (
    <header className="flex-none">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="m-0 text-[11.5px] font-bold tracking-[.14em] text-mute uppercase">
            Party discovery
          </p>
          <h1 className="mt-1.5 mb-0 text-[28px] font-extrabold leading-[1.1] tracking-[-.035em] text-ink sm:text-[32px]">
            Find your people.
          </h1>
          <p className="mt-1.5 mb-0 max-w-[540px] text-[14.5px] text-mute">
            Jump into a party, meet new teammates and make gaming better together.
          </p>
        </div>

        {narrow ? null : (
          <button
            type="button"
            onClick={onCreate}
            className={cn(
              'flex h-[46px] flex-none items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-5',
              'text-[14.5px] font-bold text-accent-fg',
              'transition-[background-color,transform] duration-[var(--t-hover)] ease-[var(--ease)]',
              'hover:bg-accent-hi active:scale-[.985]'
            )}
          >
            <Icons.Plus size={17} />
            Create party
          </button>
        )}
      </div>

      <div className="relative mt-5">
        <Icons.Search size={17} className="absolute top-1/2 left-4 -translate-y-1/2 text-mute" />
        <input
          value={typed}
          onChange={(e) => onTyped(e.target.value)}
          placeholder="Search parties, games or keywords..."
          spellCheck={false}
          aria-label="Search parties"
          className={cn(
            'h-[52px] w-full rounded-[calc(14px*var(--radius-mul,1))] border border-line-soft bg-surface-1 pr-4 pl-11 text-[14.5px] text-ink outline-none',
            'placeholder:text-ghost',
            'transition-[border-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
            'hover:border-line focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]'
          )}
        />
      </div>
    </header>
  );
}

/** One removable chip per active filter, plus a way to drop them all. */
function ActiveFilters({ game, region, search, micOnly, openOnly, catalog, onClear }: {
  game: string;
  region: string;
  search: string;
  micOnly: boolean;
  openOnly: boolean;
  catalog: ReturnType<typeof useSession.getState>['catalog'];
  onClear: (what: 'game' | 'region' | 'search' | 'mic' | 'open' | 'all') => void;
}) {
  const chips: { key: 'game' | 'region' | 'search' | 'mic' | 'open'; label: string }[] = [];

  if (game) {
    chips.push({ key: 'game', label: catalog?.games.find((g) => g.id === game)?.name ?? game });
  }
  if (region) {
    chips.push({ key: 'region', label: catalog?.regions.find((r) => r.id === region)?.name ?? region });
  }
  if (search) chips.push({ key: 'search', label: '"' + search + '"' });
  if (micOnly) chips.push({ key: 'mic', label: 'Mic required' });
  /* "Has room" is on by default, so it is only worth naming when it is off -
     and when it is off, that is the surprising state worth showing. */
  if (!openOnly) chips.push({ key: 'open', label: 'Including full parties' });

  if (!chips.length) return null;

  return (
    <div className="flex flex-none flex-wrap items-center gap-2">
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onClear(c.key)}
          className={cn(
            'flex h-[32px] items-center gap-1.5 rounded-full border border-accent-line bg-accent-soft px-3',
            'text-[12.5px] font-semibold text-accent',
            'transition-[background-color,border-color] duration-[var(--t-hover)] ease-[var(--ease)]',
            'hover:bg-[color-mix(in_srgb,var(--accent)_16%,transparent)]'
          )}
        >
          {c.label}
          <Icons.Close size={13} />
          <span className="sr-only">Remove this filter</span>
        </button>
      ))}
      <button
        type="button"
        onClick={() => onClear('all')}
        className="rounded-[calc(8px*var(--radius-mul,1))] px-2 py-1 text-[12.5px] font-bold text-mute transition-colors duration-[var(--t-hover)] ease-[var(--ease)] hover:text-accent"
      >
        Clear filters
      </button>
    </div>
  );
}

function SideRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon name={icon} size={15} className="flex-none text-ghost" />
      <dt className="flex-1 text-[12.5px] text-mute">{label}</dt>
      <dd className="text-[12.5px] font-semibold">{value}</dd>
    </div>
  );
}

/** "34m ago". Short, because neither the card nor the panel has room for a sentence. */
function ago(at: number): string {
  const s = Math.max(0, (Date.now() - at) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
}
